'use client'
import { ReactNode, memo, useCallback, useMemo, useState } from 'react'
import { useParams } from 'next/navigation'
import styled from 'styled-components'
import { motion } from 'motion/react'
import { getTransition, mq, hideScrollbar, font12_Nav, vw } from '@/styles'
import { LANG } from '@/utils'
import { useDropdown } from './useDropdown'
import { Figure } from '@/components/base/Figure'

type Props = {
  className?: string,
  trigger: ReactNode | string,
  options: {
    value: string
    path: string
  }[]
  direction: string
}

const dropDrownIcon = {
  type: 'svg' as const,
  src: '/images/svg/i--dropdown.svg',
  alt: 'dropdown icon',
  size: { width: 11, height: 5 }
}

const getOptions = (options, onClickOption, currentSelection) => {
  return options.map((option, idx) => (
    <li key={`${idx}-${option.path}`} className={option.value === currentSelection?.toUpperCase() ? 'active' : ''}>
      <a href={`${option.path}`} onClick={onClickOption} >{option.value}</a>
    </li>
  ))
}

export const Dropdown = memo<Props>(({ className = '', trigger, options, direction }) => {
  const [triggerRef, dropdownRef, expanded, toggleExpanded] = useDropdown()
  const { lang } = useParams()
  const [currentSelection, setSelection] = useState(lang ?? LANG)

  const onClickOption = useCallback((option) => {
    toggleExpanded()
    setSelection(option.value)
  }, [toggleExpanded])

  const Options = useMemo(() => getOptions(options, onClickOption, currentSelection), [options, currentSelection, onClickOption])

  return (
    <DropdownWrapper className={className} $expanded={expanded} $direction={direction}>
      <button className='triggerButton' type='button' ref={triggerRef}  onClick={() => {toggleExpanded()}}>
        <span>{trigger}</span>
        <Figure className='dropdown' media={dropDrownIcon}/>
      </button>
      <OptionsList ref={dropdownRef} initial='collapsed' variants={optionsListVariants} animate={expanded ? 'expanded' : 'collapsed'}>
        {Options}
      </OptionsList>
    </DropdownWrapper>
  )
})


const DropdownWrapper = styled.div<{ $expanded: boolean, $direction: string }>`
  display: flex;
  justify-content: flex-end;
  position: relative;
  right: 0;
  width: 45px;

  .triggerButton {
    ${font12_Nav(true)}
    align-items: center;
    color: var(--color-text);
    display: flex;
    flex-direction: row;
    padding: 0;
    gap: 5px;
    height: 18px;

    ${mq.greaterThan('tablet')} {
      ${font12_Nav(false, 400)}
    }

    ${mq.greaterThan('desktop')} {
        font-size: 12px;
    }

    span {
      font-size: 12px;
      color: var(--color-text);
      position: relative;

      ${mq.greaterThan('tablet')} {
        ${font12_Nav(false, 400)}
      }

      ${mq.greaterThan('desktop')} {
        font-size: 12px;
      }

    }

    figure, .dropdown  {
      transform: ${({ $expanded, $direction }) => $expanded && $direction === 'down' ? 'rotate(-180deg)' : $direction === 'up' && !$expanded ? 'rotate(180deg)' : 'none'};
      height: 5px;
      width: 9px;

      ${mq.greaterThan('desktop')} {
        height: ${vw(5, 'desktop')};
        width: ${vw(9, 'desktop')};
      }

      ${mq.greaterThan('desktop')} {
        height: 5px;
        width: 9px;
      }
    }
  }

  ul {
    bottom: ${({ $direction }) => $direction === 'up' ? '200%' : 'unset'};
  }
`

const OptionsList = styled(motion.ul)`
  ${hideScrollbar()}
  align-items: center;
  background: #FFF7EA;
  box-shadow: 0px 4px 24px 0px rgba(0, 0, 0, 0.15);
  display: inline-flex;
  flex-direction: column;
  gap: 10px;
  height: 0;
  right: -8px;
  opacity: 0;
  overflow-y: auto;
  padding: 10px 20px;
  position: absolute;
  top: -8px;
  width: auto;
  z-index: 10;

  ${mq.greaterThan('tablet')}{
    top: -0.65vw;
    right: -0.7vw;
  }

  ${mq.greaterThan('desktop')}{
    top: -8px;
    right: -8px;
  }

  li {
    ${font12_Nav(true)}
    color: var(--color-text);
    cursor: pointer;
    opacity: .4;
    transition: opacity 300ms ease, background-color 300ms ease;
    width: 100%;

    /* ${mq.greaterThan('nexus7')}{
      font-size: 12px;
    } */

    ${mq.greaterThan('tablet')}{
      ${font12_Nav(false, 400)}
    }

    &:active,
    &:hover,
    &.active {
      opacity: 1;
    }

    button {
      ${font12_Nav(true)}
      color: var(--color-text);

      ${mq.greaterThan('tablet')} {
        ${font12_Nav(false)}
      }
    }
  }
`

const optionsListVariants = {
  collapsed: {
    height: '0',
    opacity: 0,
    transitionEnd: {
      display: 'none',
    },
    transition: { ...getTransition(.8) }
  },
  expanded: {
    height: 'auto',
    opacity: 1,
    display: 'flex',
    transition: { ...getTransition(.8) }
  }
}
